import { z } from "zod";
import { RESULT_FAMILY_ARRAY_MAX, RESULT_RANK_MAX, START_LIST_SLOTS_MAX } from "./results";
import {
	TIMING_LANE_STATUSES,
	TIMING_PAYLOAD_SCHEMA,
	TIMING_REACTION_MAX_MS,
	TIMING_TIME_MAX_MS,
	type TimingIngestResponse,
	type TimingLane,
	type TimingPayload,
} from "./timing";
import type { FieldBenchSnapshot } from "./timing-field";

/**
 * Timing bench relay — a venue timer's latest race table, BY TIMER LANE,
 * held by the API so an HTTPS OVR can show it (the bench serves plain HTTP on
 * the venue LAN, which a browser on the public site cannot read).
 *
 *   bench ──bridge (pt8000ToBenchSnapshot | athleticsToBenchSnapshot | athleticsFieldToBenchSnapshot)──▶ PUT /timing/benches/:benchId   (Redis only)
 *   OVR ◀── GET /timing/benches[/:benchId]
 *   OVR "Apply timer results" ──▶ POST /meets/:id/timing/from-bench
 *        └─ benchSnapshotToTiming() ─▶ the SAME ingest as POST /meets/:id/timing
 *        └─ FIELD: normalizeFieldBench() ─▶ the SAME save statement as an operator's (timing-field.ts)
 *
 * Nothing is applied automatically: the snapshot is not tied to a meet, and an
 * operator's Apply on the meet they have open is the one writer.
 *
 * The snapshot keeps the bench's own reading (timer lane, legs, lap touches);
 * every decision about what is written — pool lanes, suspect lanes, relays,
 * the split distances, the finish — is `benchSnapshotToTiming`'s, so a bench
 * read live and a snapshot applied later give the same rows.
 */

export const TIMING_BENCH_ID_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
/** Benches listed / kept (the index is trimmed to the newest). */
export const TIMING_BENCHES_MAX = 20;
/** A bench not heard from for this long is forgotten. */
export const TIMING_BENCH_TTL_SECONDS = 12 * 3600;

const ms = z.number().int().min(0).max(TIMING_TIME_MAX_MS);

/**
 * What a bench times: POOL (a swimming touchpad timer — lanes counted from 0,
 * laps by pool length), TRACK (an athletics finish timer — the start list's
 * own lanes, a reaction per lane, a wind reading) or FIELD (an athletics jump
 * or throw — a card per athlete by bib, `timing-field.ts`). OVR lists a
 * swimming heat's POOL benches, a track race's TRACK benches and a field
 * event's FIELD benches. A snapshot without one is POOL (the first bridges
 * sent none).
 */
export const TIMING_BENCH_KINDS = ["POOL", "TRACK", "FIELD"] as const;
export type TimingBenchKind = (typeof TIMING_BENCH_KINDS)[number];
/** The kinds whose snapshot is a race table by timer lane (`timingBenchSnapshot`); FIELD has its own shape. */
export const LANE_BENCH_KINDS = ["POOL", "TRACK"] as const;
export type LaneBenchKind = (typeof LANE_BENCH_KINDS)[number];
export const timingBenchKindOf = (snapshot: { kind?: TimingBenchKind }): TimingBenchKind => snapshot.kind ?? "POOL";

/** Any bench's snapshot: a lane table, or a field card. */
export type BenchSnapshot = TimingBenchSnapshot | FieldBenchSnapshot;
export const isFieldBenchSnapshot = (s: BenchSnapshot): s is FieldBenchSnapshot => s.kind === "FIELD";
export const isLaneBenchSnapshot = (s: BenchSnapshot): s is TimingBenchSnapshot => s.kind !== "FIELD";

/** The wind gauge's range (m/s) — the meet detail's `wind_mps` range. */
export const TIMING_BENCH_WIND_MAX_MPS = 20;

export const TIMING_BENCH_LEGS_MAX = 8;

/** The pool wall a touch is at: S = the start wall, T = the turning wall. */
export const POOL_WALL_SIDES = ["S", "T"] as const;
export type PoolWallSide = (typeof POOL_WALL_SIDES)[number];

export const timingBenchLeg = z
	.object({
		/**
		 * 1-based, the bench's own numbering: a leg runs from the start wall
		 * back to it (100 m in a 50 m pool), so an individual 200 m has two.
		 * It is NOT a relay leg — distances come from the walls (`poolLaneTouches`).
		 */
		leg: z.number().int().min(1).max(TIMING_BENCH_LEGS_MAX),
		/** The lane has finished — `to_ms` is the finish. */
		final: z.boolean(),
		/** The touch at the end of the leg (cumulative race time), when the bench has one. */
		to_ms: ms.nullable(),
		/**
		 * Lap touches of this leg, cumulative race time. `lap` / `side` are the
		 * timer's (`poolWallOf`); no `side` (relays older than it) reads T — a
		 * touch inside a leg is at the turning wall.
		 */
		splits: z
			.array(z.object({ lap: z.number().int().min(0).max(1000), cumulative_ms: ms, side: z.enum(POOL_WALL_SIDES).nullable().optional() }).strict())
			.max(RESULT_FAMILY_ARRAY_MAX),
	})
	.strict();
export type TimingBenchLeg = z.infer<typeof timingBenchLeg>;

export const timingBenchLane = z
	.object({
		/** The timer's lane, 0-based. */
		timer_lane: z.number().int().min(0).max(99),
		/** The pool lane once the bench's lane offset is set, else null (such a lane is never applied). */
		pool_lane: z.number().int().min(0).max(99).nullable(),
		legs: z.array(timingBenchLeg).max(TIMING_BENCH_LEGS_MAX),
		/** The bench's finish touch place — only compared by the server, never stored. */
		place: z.number().int().min(1).max(RESULT_RANK_MAX).nullable(),
		/** The bench doubts a reading of this lane: it is never applied (the operator keys it in). */
		suspect: z.boolean(),
		reasons: z.array(z.string().max(200)).max(10),
		/** Simulator only (irm / dq); real hardware sends none. */
		status: z.enum(TIMING_LANE_STATUSES).nullable(),
		/** TRACK: the start reaction (false-start system), when the bench has one. */
		reaction_ms: z.number().int().min(0).max(TIMING_REACTION_MAX_MS).nullable().optional(),
	})
	.strict();
export type TimingBenchLane = z.infer<typeof timingBenchLane>;

/** One bench's latest race table, snake_case on the wire. */
export const timingBenchSnapshot = z
	.object({
		/** e.g. `SEIKO_PT8000` — the same rule as `timingPayload.source.system`. */
		system: z.string().trim().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
		device_id: z.string().trim().min(1).max(64).optional(),
		simulated: z.boolean(),
		/** When the bridge read it. */
		captured_at: z.string().datetime({ offset: true }),
		clock: z.object({ ms, running: z.boolean() }).strict().nullable(),
		/** The bench's reset counter: it moves when the timer starts a new race. */
		resets: z.number().int().min(0),
		warnings: z.array(z.string().max(200)).max(20),
		/** The simulator's track settings; real hardware sends none. */
		pool_length_m: z.number().positive().max(100).nullable(),
		distance_m: z.number().positive().max(100_000).nullable(),
		relay_legs: z.number().int().min(1).max(TIMING_BENCH_LEGS_MAX).nullable(),
		lanes: z.array(timingBenchLane).max(START_LIST_SLOTS_MAX * 2),
		/** Absent = POOL (`timingBenchKindOf`). */
		kind: z.enum(LANE_BENCH_KINDS).optional(),
		/** TRACK: the race's wind reading (m/s); null = none measured ("nwi"). Shown to the operator, never written by Apply. */
		wind_mps: z.number().min(-TIMING_BENCH_WIND_MAX_MPS).max(TIMING_BENCH_WIND_MAX_MPS).nullable().optional(),
		/** What race the bench is on, in its own words ("Event 1011 · Heat 3 · Men's 100m") — so the operator can tell it is THIS race. */
		race_label: z.string().trim().max(120).optional(),
	})
	.strict();
export type TimingBenchSnapshot = z.infer<typeof timingBenchSnapshot>;

/** A row of `GET /timing/benches`, newest first. */
export interface TimingBenchSummary {
	bench_id: string;
	kind: TimingBenchKind;
	system: string;
	simulated: boolean;
	captured_at: string;
	received_at: string;
}

/** `GET /timing/benches/:benchId`. */
export interface TimingBenchDetail {
	bench_id: string;
	received_at: string;
	snapshot: BenchSnapshot;
}

/** `PUT /timing/benches/:benchId`. */
export interface TimingBenchPutResponse {
	bench_id: string;
	/** When the stored content was first received (an unchanged PUT keeps it). */
	received_at: string;
}

/** `POST /meets/:id/timing/from-bench`. */
export const timingFromBenchBody = z
	.object({
		bench_id: z.string().regex(TIMING_BENCH_ID_PATTERN),
		/** Overrides the bench's pool length (else the snapshot's, else 50 m). */
		pool_length_m: z.number().positive().max(100).optional(),
		/**
		 * The reading the operator saw (`snapshot.captured_at`): when the bench
		 * has sent a newer one since, nothing is applied (409
		 * `TIMING_BENCH_CHANGED`). Absent = apply the latest.
		 */
		captured_at: z.string().datetime({ offset: true }).optional(),
	})
	.strict();
export type TimingFromBenchBody = z.infer<typeof timingFromBenchBody>;

export const TIMING_BENCH_POOL_LENGTH_DEFAULT_M = 50;

/**
 * The timer is on another race: it says its distance (the simulator, a track
 * console) and that is not this race's. Null when they agree or either side
 * does not say (real PT-8000 hardware sends no distance). The API refuses such
 * an Apply (409 `TIMING_BENCH_RACE_MISMATCH`); OVR says so before the click.
 */
export function benchDistanceMismatch(
	snapshot: Pick<TimingBenchSnapshot, "distance_m">,
	raceDistanceM: number | null,
): { timerDistanceM: number; raceDistanceM: number } | null {
	const timer = snapshot.distance_m;
	if (timer === null || raceDistanceM === null || timer === raceDistanceM) return null;
	return { timerDistanceM: timer, raceDistanceM };
}

export const TIMING_BENCH_SKIP_REASONS = ["no_pool_lane", "suspect"] as const;
export type TimingBenchSkipReason = (typeof TIMING_BENCH_SKIP_REASONS)[number];

export interface TimingBenchSkip {
	/** The timer lane (0-based) when there is no pool lane, else the pool lane. */
	lane: number;
	reason: TimingBenchSkipReason;
	/** The bench's own words, for `suspect`. */
	details?: string[];
}

/** `POST /meets/:id/timing/from-bench`: the ingest's answer, plus what the bench had that was not sent. */
export interface TimingFromBenchResponse extends TimingIngestResponse {
	bench_id: string;
	captured_at: string;
	skipped: TimingBenchSkip[];
}

export interface TimingBenchOptions {
	meetId: string;
	distanceM: number;
	poolLengthM: number;
	sequence: number;
	/** ISO 8601 with an offset. */
	sentAt: string;
	/** Defaults to the snapshot's `device_id`. */
	deviceId?: string;
}

export interface TimingBenchResult {
	payload: TimingPayload | null;
	skipped: TimingBenchSkip[];
}

/**
 * The wall a pool timer's touch is at, 0-based — wall n is (n + 1) pool
 * lengths from the start. The PT-8000 does not number lengths: `lap` is 0 at
 * the first turn and goes up each time the swimmer is back at the start wall,
 * and `side` says which wall. So a 200 m in a 50 m pool touches 0T, 1S, 1T,
 * 2S = walls 0, 1, 2, 3 (TSR's `wallOf`). No side reads T (see `timingBenchLeg`).
 */
export const poolWallOf = (lap: number, side: PoolWallSide | null | undefined): number => (side === "S" ? 2 * lap - 1 : 2 * lap);

export interface PoolLaneTouches {
	/** Every touch before the finish, by distance (one per distance), cumulative race time. */
	touches: { distanceM: number; ms: number }[];
	/** The last leg's `to_ms` once it is final (and not short of the race distance), else null. */
	finishMs: number | null;
}

/**
 * One lane's timer touches in metres — the ONE rule behind "Apply timer
 * results" (`benchSnapshotToTiming`) and OVR's Timer column:
 *
 * - a lap touch is at (`poolWallOf(lap, side)` + 1) × pool length;
 * - a leg that is complete (`final`, or a later leg exists) ends at the wall
 *   after its last lap touch — the start wall (a leg with no lap touch: two
 *   lengths after the leg before it);
 * - the LAST leg, once `final`, is the finish — unless its lap touches put
 *   its end short of the race distance (a relay swimmer's leg marked final
 *   before the next one touches is a changeover). A leg with no lap touch
 *   (a TRACK race: one leg, its finish) is the finish. A last leg still
 *   swimming says nothing at its end (its `to` may be the running time).
 * - with no final leg, a lap touch AT the race distance is the finish: the
 *   bench closes legs only at the start wall, so a 50 m in a 50 m pool ends
 *   on a turning-wall touch in a leg it never closes.
 *
 * Legs are never read as relay legs: the bench cuts every race at the start
 * wall (a 200 m is two legs), and a relay's changeovers are start-wall
 * touches like any other — so one rule serves both.
 */
export function poolLaneTouches(legs: readonly TimingBenchLeg[], poolLengthM: number, distanceM: number | null): PoolLaneTouches {
	const ordered = [...legs].sort((a, b) => a.leg - b.leg);
	const byDistance = new Map<number, number>();
	let finishMs: number | null = null;
	let endWall = -1;
	for (const [i, leg] of ordered.entries()) {
		let lastWall: number | null = null;
		for (const s of leg.splits) {
			const wall = poolWallOf(s.lap, s.side);
			if (wall < 0) continue;
			byDistance.set((wall + 1) * poolLengthM, s.cumulative_ms);
			lastWall = lastWall === null ? wall : Math.max(lastWall, wall);
		}
		endWall = lastWall === null ? endWall + 2 : lastWall + 1;
		const isLast = i === ordered.length - 1;
		if (leg.to_ms === null || (isLast && !leg.final)) continue;
		const endM = (endWall + 1) * poolLengthM;
		if (isLast && (lastWall === null || distanceM === null || endM >= distanceM)) finishMs = leg.to_ms;
		else byDistance.set(endM, leg.to_ms);
	}
	// The wall at the race distance IS the finish, whatever leg the bench left open
	// (a 50 m in a 50 m pool ends at the turning wall, a leg it never closes).
	const atDistance = distanceM === null ? undefined : byDistance.get(distanceM);
	if (finishMs === null && atDistance !== undefined) {
		finishMs = atDistance;
		byDistance.delete(distanceM as number);
	}
	return {
		touches: [...byDistance.entries()].sort((a, b) => a[0] - b[0]).map(([distanceM, ms]) => ({ distanceM, ms })),
		finishMs,
	};
}

/**
 * A bench snapshot → one timing payload for a meet, and the lanes it could not
 * send. Pure. The rules:
 *
 * - **No pool lane** (the bench's lane offset not set) → skipped
 *   (`no_pool_lane`) — guessing the offset would write the wrong swimmer.
 * - **A suspect lane is skipped whole** (`suspect`, with the bench's reasons).
 * - **Touches and the finish are `poolLaneTouches`'**; a touch at or beyond
 *   the race distance is dropped (the wall at the distance IS the finish).
 * - **The finish is sent only from a `final` reading.** A lane still
 *   swimming sends its splits, no finish.
 * - **`place`** goes out only with a finish.
 * - **`reaction_ms`** (TRACK) goes out with the lane's reading, or alone.
 * - **A lane with a status or a reaction and no reading** is still sent.
 * - **No surviving lane → `payload: null`**: the endpoint needs ≥ 1 lane.
 */
export function benchSnapshotToTiming(snapshot: TimingBenchSnapshot, opts: TimingBenchOptions): TimingBenchResult {
	const skipped: TimingBenchSkip[] = [];
	const pool = opts.poolLengthM;
	const distance = opts.distanceM;

	const ordered = [...snapshot.lanes].sort((a, b) => a.timer_lane - b.timer_lane);
	const withLegs = ordered.filter((l) => l.legs.length > 0);

	const lanes: TimingLane[] = [];
	for (const bl of withLegs) {
		if (bl.pool_lane === null) {
			skipped.push({ lane: bl.timer_lane, reason: "no_pool_lane" });
			continue;
		}
		if (bl.suspect) {
			skipped.push({ lane: bl.pool_lane, reason: "suspect", details: [...bl.reasons] });
			continue;
		}
		const { touches, finishMs: finish } = poolLaneTouches(bl.legs, pool, distance);
		const splits = touches.filter((t) => t.distanceM < distance).map((t) => ({ distance_m: t.distanceM, cumulative_ms: t.ms }));

		const lane: TimingLane = { lane: bl.pool_lane };
		if (splits.length > 0) lane.splits = splits;
		if (finish !== null) {
			lane.finish_ms = finish;
			if (bl.place !== null) lane.place = bl.place;
		}
		if (bl.reaction_ms != null) lane.reaction_ms = bl.reaction_ms;
		if (bl.status) lane.status = bl.status;
		lanes.push(lane);
	}

	// A lane marked DNS / DNF / DSQ that never touched, or a sprinter off the blocks not yet in: status / reaction only.
	for (const bl of ordered) {
		if (bl.legs.length > 0 || (!bl.status && bl.reaction_ms == null)) continue;
		if (bl.pool_lane === null) {
			skipped.push({ lane: bl.timer_lane, reason: "no_pool_lane" });
			continue;
		}
		const lane: TimingLane = { lane: bl.pool_lane };
		if (bl.reaction_ms != null) lane.reaction_ms = bl.reaction_ms;
		if (bl.status) lane.status = bl.status;
		lanes.push(lane);
	}

	if (lanes.length === 0) return { payload: null, skipped };
	lanes.sort((a, b) => Number(a.lane) - Number(b.lane));
	const deviceId = opts.deviceId ?? snapshot.device_id;
	return {
		payload: {
			schema: TIMING_PAYLOAD_SCHEMA,
			source: deviceId ? { system: snapshot.system, device_id: deviceId } : { system: snapshot.system },
			race: { meet_id: opts.meetId, distance_m: distance },
			sequence: opts.sequence,
			sent_at: opts.sentAt,
			lanes,
		},
		skipped,
	};
}
